import type { PreferenceField } from "@/features/legal/types/onboarding.type";

export type OnboardingStepKey = "security" | "legal" | "preferences" | "ready";

export const ONBOARDING_STEP_LABELS: Record<OnboardingStepKey | "login", string> = {
  login: "Connexion",
  security: "Sécurité",
  legal: "Conditions",
  preferences: "Préférences",
  ready: "C'est parti",
};

export type OnboardingShowcaseTitle = {
  lines: readonly string[]
  accent: string
};

// Hero of the mascot panel next to each step, in the voice of the login page.
export const ONBOARDING_SHOWCASE_TITLES: Record<OnboardingStepKey, OnboardingShowcaseTitle> = {
  security: { lines: ["Votre compte."], accent: "Bien gardé." },
  legal: { lines: ["Quelques règles."], accent: "Beaucoup d'avantages." },
  preferences: { lines: ["Vos choix."], accent: "Vos règles." },
  ready: { lines: ["Tout est prêt."], accent: "À vous de jouer." },
};

export const ONBOARDING_COPY = {
  PROGRESS_LABEL: "Votre parcours",
  STEP_CAPTION: (position: number, total: number, label: string) => `Étape ${position} sur ${total} · ${label}`,
  // Before the account's next steps are known, the total would be a guess.
  STEP_CAPTION_WITHOUT_TOTAL: (position: number, label: string) => `Étape ${position} · ${label}`,
  LOADING_SPACE: "Chargement de votre espace…",
  SECURITY_FOOTNOTE:
    "Ce code protège votre accès. Il est indépendant de vos choix concernant les SMS du CSE et la visibilité de votre numéro.",
} as const;

export const PREFERENCES_COPY = {
  STEP_CAPTION: "Facultatif",
  TITLE: "Gardons le contact.",
  TITLE_ACCENT: "À votre façon.",
  INTRO: "Choisissez comment votre CSE peut utiliser votre numéro. Vous pouvez laisser toutes les options désactivées et continuer.",
  PHONE_LABEL: "Votre numéro de portable",
  PHONE_BADGE: "Déjà renseigné",
  CSE_GROUP: "Avec votre CSE",
  YEATI_GROUP: "Avec Yeati",
  FOOTNOTE: "Ces choix sont indépendants. Ils ne concernent pas les messages nécessaires à la sécurité et au fonctionnement du service.",
  SAVE: "Enregistrer mes choix et continuer",
  SAVE_ERROR: "Vos préférences n'ont pas été enregistrées. Vos choix restent affichés ; réessayez.",
} as const;

type PreferenceDefinition = {
  field: PreferenceField
  group: "cse" | "yeati"
  title: string
  description: string
  summaryLabel: string
  summaryOn: string
  summaryOff: string
};

export const PREFERENCE_DEFINITIONS: readonly PreferenceDefinition[] = [
  {
    field: "allow_use_cell_phone",
    group: "cse",
    title: "Recevoir les SMS de mon CSE",
    description: "J'autorise mon CSE à m'envoyer des SMS pour ses communications.",
    summaryLabel: "SMS de votre CSE",
    summaryOn: "Autorisés",
    summaryOff: "Non autorisés",
  },
  {
    field: "allow_show_cell_phone",
    group: "cse",
    title: "Partager mon numéro avec mon CSE",
    description: "J'autorise les personnes habilitées de mon CSE à voir mon numéro de portable.",
    summaryLabel: "Numéro visible par le CSE",
    summaryOn: "Oui",
    summaryOff: "Non",
  },
  {
    field: "allow_email_from_yeati",
    group: "yeati",
    title: "Recevoir les bons plans Yeati par e-mail",
    description: "Je souhaite recevoir les actualités et offres commerciales de Yeati. Désinscription à tout moment.",
    summaryLabel: "Bons plans Yeati par e-mail",
    summaryOn: "Oui",
    summaryOff: "Non",
  },
];

export const READY_COPY = {
  TITLE: "Votre espace vous attend.",
  INTRO: "Vos conditions sont acceptées et vos préférences sont enregistrées.",
  CGU_SUMMARY_LABEL: (versionLabel: string) => `CGU · version ${versionLabel}`,
  CGU_ACCEPTED: "Acceptées",
  ENTER: "Découvrir mon espace",
  EDIT_PREFERENCES: "Modifier mes préférences",
} as const;
